import React, { useRef } from "react";

import { Page } from "ComparePlans/Page";
import isIntuit from "Intuit/isIntuit";
import EnrolledPolicyPlansLoader from "QuotePage/Components/EnrolledPolicyPlansLoader";
import {
  hasPlansBeforeInitialData,
  getMedicalPlanEnrolledId,
  getMedicalPlanRenewalId,
  getPlanDeviationProps,
  getSavedPlans,
  getPayrollLead,
} from "QuotePage/Selectors";
import { quotePageName } from "QuotePage/urls";
import { connect, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { trackStayWithDefaultRenewal } from "Renewal/utils";
import AnalyticsTracking from "Utils/AnalyticsTracking";

import { LinkButton } from "Cobalt/Button";
import Button from "Common/Button";
import { addPlanToCart } from "Common/CartService";
import EnterAgesModal from "Common/EnterAges/Components/EnterAgesModal";
import { isSampleQuote } from "Common/EnterAges/Selectors";
import { Ul, Li } from "Common/HTML";
import Icon from "Common/Icon";
import ScrollToTopOnMount from "Common/ScrollToTopOnMount";
import { inABTest } from "Common/Selectors";

import type { Dispatch } from "redux";
import type { MedicalPlan } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("ComparePage");

const SelectPlanButton = ({ plan, onStartApplicationClicked, disableButtons, payrollLead }) => {
  const renewalPlanId = useSelector(getMedicalPlanRenewalId);
  if (payrollLead?.partner) {
    return (
      <LinkButton size={isIntuit() ? "medium" : undefined} fullWidth href={`/${payrollLead.partner}/router/payroll-prospect`}>
        Get Started
      </LinkButton>
    );
  }

  return (
    <Button
      size={isIntuit() ? "medium" : undefined}
      disabled={disableButtons}
      fullWidth
      onClick={() => {
        if (plan.id === renewalPlanId) {
          trackStayWithDefaultRenewal({ location: "compare_plans" });
        }
        onStartApplicationClicked(plan, { location: "compare_plans" });
      }}
    >
      Get Started
    </Button>
  );
};
const ButtonArea = ({ isSampleQuote, onGetPreciseQuote, onStartApplicationClicked, plan, disableButtons, medicalPlanEnrolledId, payrollLead }) => (
  <Ul className={bem("ButtonArea")}>
    {medicalPlanEnrolledId != plan.id && (
      <Li className={isIntuit() ? bem("ButtonArea-item-intuit") : bem("ButtonArea-item")} py={isIntuit() ? 0 : 2} pr={2}>
        <SelectPlanButton plan={plan} onStartApplicationClicked={onStartApplicationClicked} disableButtons={disableButtons} payrollLead={payrollLead} />
      </Li>
    )}
  </Ul>
);

const ComparePage = ({
  plans,
  onGetPreciseQuote,
  onClickBackToQuotePage,
  onStartApplicationClicked,
  isSampleQuote,
  disableButtons,
  medicalPlanEnrolledId,
  shouldShowSimplePlanName,
  planDeviationProps,
  payrollLead,
}) => {
  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const topHeading = [
    <Button color="link" onClick={onClickBackToQuotePage}>
      <Icon icon="chevron-left" style={{ marginRight: 5 }} /> Back
    </Button>,
    <Button
      color="link"
      onClick={() => {
        AnalyticsTracking.trackEvent("print_decision_packet", {
          location: "compare_plans_page",
        });

        handlePrint();
      }}
    >
      <Icon icon="print" style={{ marginRight: 5 }} flavor="solid" /> Print
    </Button>,
  ];

  return (
    <div>
      <ScrollToTopOnMount />
      <Page
        ref={componentRef}
        plans={plans}
        isSampleQuote={isSampleQuote}
        topHeading={topHeading}
        shouldShowSimplePlanName={shouldShowSimplePlanName}
        isInRenewal={medicalPlanEnrolledId === null}
        planDeviationProps={planDeviationProps}
        planButtons={(plan) => (
          <ButtonArea
            isSampleQuote={isSampleQuote}
            onGetPreciseQuote={onGetPreciseQuote}
            onStartApplicationClicked={onStartApplicationClicked}
            plan={plan}
            medicalPlanEnrolledId={medicalPlanEnrolledId}
            disableButtons={disableButtons}
            payrollLead={payrollLead}
          />
        )}
      />

      <EnterAgesModal />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const loadedPlans = [...getSavedPlans(state), ...(ownProps.enrolledPlans || [])].reduce((acc, plan) => {
    if (plan) {
      acc[plan.id] = plan;
    }
    return acc;
  }, {});
  const plans = state.savedPlanIDs.map((planID) => loadedPlans[planID]);
  return {
    payrollLead: getPayrollLead(state),
    plans,
    onClickBackToQuotePage: () => {
      AnalyticsTracking.trackEvent("cta_clicked", {
        location: "compare_page",
        label: "back",
      });

      ownProps.history.push(quotePageName());
    },
    isSampleQuote: isSampleQuote(state),
    disableButtons: hasPlansBeforeInitialData(state),
    medicalPlanEnrolledId: getMedicalPlanEnrolledId(state),
    shouldShowSimplePlanName: inABTest(state, "plan_name_simplification"),
    planDeviationProps: getPlanDeviationProps(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    onStartApplicationClicked: (plan: MedicalPlan, tracking: any = {}) => {
      dispatch(
        addPlanToCart({
          planType: "medical",
          plan,
          tracking,
          shouldClearDentalVision: false,
          continueToCart: true,
          shouldClearLife: false,
        }),
      );
    },
  };
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

const ConnectedComparePage = withRouter(withRedux(ComparePage));

/**
 * This component is only used in the QuotePage App,
 * So loadding the enrolledPlans here to avoid obfuscation in the QuotePage App file.
 */
const HydratedComparePage = (props) => (
  <EnrolledPolicyPlansLoader>
    {({ medicalPlans: enrolledPlans }) => <ConnectedComparePage {...props} enrolledPlans={enrolledPlans} />}
  </EnrolledPolicyPlansLoader>
);

export default HydratedComparePage;
